import { useState, useEffect } from 'react'

const RECENT_SEARCHES_KEY = 'hubble_recent_user_searches'
const MAX_STORED_SEARCHES = 20
const INITIAL_DISPLAY_COUNT = 5

interface SearchEntry {
  query: string
  timestamp: number
}

interface UseRecentSearchesReturn {
  recentSearches: SearchEntry[]
  addRecentSearch: (search: string) => void
  clearRecentSearches: () => void
  removeRecentSearch: (search: string) => void
}

const useRecentSearches = (): UseRecentSearchesReturn => {
  const [recentSearches, setRecentSearches] = useState<SearchEntry[]>([])

  // Function to load from localStorage
  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          // Handle migration from old format (array of strings) to new format (array of objects)
          const migrated = parsed.map((item) => {
            if (typeof item === 'string') {
              return { query: item, timestamp: Date.now() }
            }
            return item
          })
          setRecentSearches(migrated)
        }
      }
    } catch (error) {
      console.error('Error loading recent searches:', error)
    }
  }

  // Load recent searches from localStorage on mount and poll for updates
  useEffect(() => {
    loadFromLocalStorage()

    // Poll for changes every 500ms
    const interval = setInterval(() => {
      loadFromLocalStorage()
    }, 500)

    return () => {
      clearInterval(interval)
    }
  }, [])

  // Save to localStorage whenever recentSearches changes
  useEffect(() => {
    if (recentSearches.length > 0) {
      try {
        localStorage.setItem(
          RECENT_SEARCHES_KEY,
          JSON.stringify(recentSearches)
        )
      } catch (error) {
        console.error('Error saving recent searches:', error)
      }
    }
  }, [recentSearches])

  const addRecentSearch = (search: string) => {
    const trimmedSearch = search.trim()
    if (!trimmedSearch) return

    setRecentSearches((prev) => {
      // Remove if already exists
      const filtered = prev.filter((s) => s.query !== trimmedSearch)
      // Add to beginning with timestamp
      const newEntry: SearchEntry = {
        query: trimmedSearch,
        timestamp: Date.now(),
      }
      const updated = [newEntry, ...filtered]
      // Keep only MAX_STORED_SEARCHES items
      return updated.slice(0, MAX_STORED_SEARCHES)
    })
  }

  const clearRecentSearches = () => {
    setRecentSearches([])
  }

  const removeRecentSearch = (search: string) => {
    setRecentSearches((prev) => prev.filter((s) => s.query !== search))
  }

  return {
    recentSearches,
    addRecentSearch,
    clearRecentSearches,
    removeRecentSearch,
  }
}

export default useRecentSearches
export { INITIAL_DISPLAY_COUNT }
